import { type AdminOrderDestination, ORDER_COURIERS } from '@repo/api-contracts';
import { useId } from 'react';
import { orderDestinationLabel } from '@/entities/order';
import { Button, formStyles, Input, Label } from '@/shared/ui';

interface ShipmentRow {
  readonly id: number;
  /** 이 송장이 가는 배송지 — 빈 문자열이면 아직 고르지 않음. */
  readonly destinationId: string;
  readonly kind: 'PARCEL' | 'DIRECT';
  readonly courierCode: string;
  readonly trackingNumber: string;
  readonly directMethodNote: string;
}

interface OrderShipmentFieldsProps {
  readonly row: ShipmentRow;
  readonly number: number;
  /** 주문 배송지 — 2곳 이상일 때만 배송지 선택 칸을 보인다(1곳이면 폼이 자동 선택). */
  readonly destinations: readonly AdminOrderDestination[];
  readonly onChange: (row: ShipmentRow) => void;
  readonly onRemove: () => void;
}

/** 송장 입력 한 행 — 배송지(2곳 이상일 때), 배송 방법(택배: 택배사·송장 번호 / 직접 전달: 방법 메모)과 행 삭제. */
export function OrderShipmentFields({
  row,
  number,
  destinations,
  onChange,
  onRemove,
}: OrderShipmentFieldsProps) {
  const id = useId();
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-foreground/[0.08] p-4">
      {destinations.length > 1 ? (
        <div className={formStyles.field}>
          <Label htmlFor={`${id}-destination`}>송장 {number} 배송지</Label>
          <select
            id={`${id}-destination`}
            className={formStyles.select}
            value={row.destinationId}
            onChange={(event) => onChange({ ...row, destinationId: event.target.value })}
          >
            <option value="">선택</option>
            {destinations.map((destination, index) => (
              <option key={destination.id} value={destination.id}>
                {orderDestinationLabel(destination, index)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className={formStyles.field}>
        <Label htmlFor={`${id}-kind`}>배송 방법 {number}</Label>
        <select
          id={`${id}-kind`}
          className={formStyles.select}
          value={row.kind}
          onChange={(event) =>
            onChange({ ...row, kind: event.target.value === 'DIRECT' ? 'DIRECT' : 'PARCEL' })
          }
        >
          <option value="PARCEL">택배</option>
          <option value="DIRECT">직접 전달</option>
        </select>
      </div>
      {row.kind === 'PARCEL' ? (
        <>
          <div className={formStyles.field}>
            <Label htmlFor={`${id}-courier`}>택배사 {number}</Label>
            <select
              id={`${id}-courier`}
              className={formStyles.select}
              value={row.courierCode}
              onChange={(event) => onChange({ ...row, courierCode: event.target.value })}
            >
              <option value="">선택</option>
              {ORDER_COURIERS.map((courier) => (
                <option key={courier.code} value={courier.code}>
                  {courier.name}
                </option>
              ))}
            </select>
          </div>
          <div className={formStyles.field}>
            <Label htmlFor={`${id}-tracking`}>송장 번호 {number}</Label>
            <Input
              id={`${id}-tracking`}
              value={row.trackingNumber}
              onChange={(event) => onChange({ ...row, trackingNumber: event.target.value })}
            />
          </div>
        </>
      ) : (
        <div className={formStyles.field}>
          <Label htmlFor={`${id}-note`}>방법 메모 {number}</Label>
          <textarea
            id={`${id}-note`}
            className={formStyles.textarea}
            value={row.directMethodNote}
            onChange={(event) => onChange({ ...row, directMethodNote: event.target.value })}
          />
        </div>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-end text-destructive"
        onClick={onRemove}
      >
        행 {number} 삭제
      </Button>
    </section>
  );
}
